import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery } from 'redux-saga/effects';
import { Messages } from '../../../../codegen/Localize';
import { ISubscriptionInfo } from '../../../../ts/models/ISubscriptionInfo';
import { Set } from '../../../../ts/models/Set';
import {
  openChangeSubscriptionsFormatDialog,
} from '../../../content/SubscriptionChangeFormatDialog';
import { ChangeSubscriptionDataConditionAction } from '../../../../dda/services/ChangeSubscriptionDataConditionAction';
import { ChangeSubscriptionSubjectAction } from '../../../../dda/services/ChangeSubscriptionSubjectAction';
import { DeleteSubscriptionsAction } from '../../../../dda/services/DeleteSubscriptionsAction';
import { ResumeSubscriptionAction } from '../../../../dda/services/ResumeSubscriptionAction';
import {
  ChangeSubscriptionDataCondition,
  ChangeSubscriptionsFormat,
  ChangeSubscriptionsFrequency,
  ChangeSubscriptionsSchedule,
  ChangeSubscriptionSubject,
  DeleteSubscriptions,
  ResumeSubscription
} from '../../../actions/AppActionTypes';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import {
  ChangeSubscriptionDataConditionAction as ChangeSubscriptionDataConditionReduxAction,
  ChangeSubscriptionsFormatAction as ChangeSubscriptionsFormatReduxAction,
  ChangeSubscriptionsFrequencyAction as ChangeSubscriptionsFrequencyReduxAction,
  ChangeSubscriptionsScheduleAction as ChangeSubscriptionsScheduleReduxAction,
  ChangeSubscriptionSubjectAction as ChangeSubscriptionSubjectReduxAction,
  DeleteSubscriptionsAction as DeleteSubscriptionsReduxAction,
  ResumeSubscriptionAction as ResumeSubscriptionReduxAction
} from '../../../actions/spaces/SubscriptionsActions';
import { ServiceLocator } from '../../../ServiceLocator';
import { ChangeScheduleActionService } from '../../../../ts/services/ChangeScheduleAction';


export function* handleChangeSubscriptionSubject(action: ChangeSubscriptionSubjectReduxAction) {
  try {
    yield call(changeSubscriptionSubject, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionSubjectFailure()));
  }
}

export function changeSubscriptionSubject(subscription: ISubscriptionInfo) {
  return ChangeSubscriptionSubjectAction.changeSubject(subscription);
}

export function* handleChangeSubscriptionsSchedule(action: ChangeSubscriptionsScheduleReduxAction) {
  try {
    yield call(changeSubscriptionsSchedule, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionScheduleFailure()));
  }
}

export function changeSubscriptionsSchedule(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ChangeScheduleActionService
      .forSubscriptions(Set.fromIdItemArray(subscriptions), context);
}

export function* handleChangeSubscriptionDataCondition(action: ChangeSubscriptionDataConditionReduxAction) {
  try {
    yield call(changeSubscriptionDataCondition, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionDataConditionFailure()));
  }
}

export function changeSubscriptionDataCondition(subscription: ISubscriptionInfo) {
  return ChangeSubscriptionDataConditionAction.changeDataCondition(subscription);
}

export function* handleDeleteSubscriptions(action: DeleteSubscriptionsReduxAction) {
  try {
    yield call(deleteSubscriptions, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.deleteSubscriptionFailure()));
  }
}

export function deleteSubscriptions(subscriptions: ISubscriptionInfo[]) {
  return DeleteSubscriptionsAction.deleteSubscriptions(Set.fromIdItemArray(subscriptions));
}

export function* handleResumeSubscription(action: ResumeSubscriptionReduxAction) {
  try {
    yield call(resumeSubscription, action.subscription);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.resumeSubscriptionFailure()));
  }
}

export function resumeSubscription(subscription: ISubscriptionInfo) {
  return ResumeSubscriptionAction.resumeSubscription(subscription);
}

export function* handleChangeSubscriptionsFormat(action: ChangeSubscriptionsFormatReduxAction) {
  try {
    yield call(openChangeSubscriptionsFormatDialog, action.subscriptions);
    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    // The ReactConfirmActionDialog throws an exception when it is closed or dismissed
    // by normal means. As a result, we choose to fail silently here, rather than
    // pop an error toast when the dialog is closed properly
    return;
  }
}

export function* handleChangeSubscriptionsFrequency(action: ChangeSubscriptionsFrequencyReduxAction) {
  try {
    yield call(changeSubscriptionsFrequency, action.subscriptions);

    yield call(refreshAfterSubscriptionActionSucceeded);
  } catch(e) {
    yield put(requestFailed(e, Messages.changeSubscriptionsFrequencyFailure()));
  }
}

export function changeSubscriptionsFrequency(subscriptions: ISubscriptionInfo[]) {
  const context = ServiceLocator.getAngularActionContext();
  return ChangeScheduleActionService.changeSubscriptionsFrequency(Set.fromIdItemArray(subscriptions), context);
}

export function* refreshAfterSubscriptionActionSucceeded() {
  yield put(requestSpaceContents());
}

export function* rootSubscriptionSagas(): SagaIterator {
  yield takeEvery(ChangeSubscriptionSubject, handleChangeSubscriptionSubject);
  yield takeEvery(ChangeSubscriptionsSchedule, handleChangeSubscriptionsSchedule);
  yield takeEvery(ChangeSubscriptionDataCondition, handleChangeSubscriptionDataCondition);
  yield takeEvery(DeleteSubscriptions, handleDeleteSubscriptions);
  yield takeEvery(ResumeSubscription, handleResumeSubscription);
  yield takeEvery(ChangeSubscriptionsFormat, handleChangeSubscriptionsFormat);
  yield takeEvery(ChangeSubscriptionsFrequency, handleChangeSubscriptionsFrequency);
}
